/**
 * Master Data UI routes — one dedicated page per category (rather than tabs
 * on a single shared page), so each shows up as its own nav item, grouped
 * under the "Master Data" section header (see PLUGIN_NAV_METADATA in
 * apps/web-app's useNavigation.ts).
 */
import { defineRoutes } from '@lognai/plugin-sdk';

export const pluginId = 'master-data';

export const routes = defineRoutes(pluginId, '/master-data', [
  {
    path: '/incoterms',
    component: () => import('./pages/IncotermsPage').then((m) => ({ default: m.IncotermsPage })),
    name: 'Incoterms',
    description: 'Manage Incoterms® 2020 delivery/risk-transfer rules',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'Scale', size: 20 }, showInNav: true, order: 1, audience: 'internal' },
  },
  {
    path: '/container-types',
    component: () => import('./pages/ContainerTypesPage').then((m) => ({ default: m.ContainerTypesPage })),
    name: 'Container Types',
    description: 'Manage commercial container size/type codes',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'Container', size: 20 }, showInNav: true, order: 2, audience: 'internal' },
  },
  {
    path: '/currencies',
    component: () => import('./pages/CurrenciesPage').then((m) => ({ default: m.CurrenciesPage })),
    name: 'Currencies',
    description: 'Manage ISO 4217 currency codes',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'DollarSign', size: 20 }, showInNav: true, order: 3, audience: 'internal' },
  },
  {
    path: '/countries',
    component: () => import('./pages/CountriesPage').then((m) => ({ default: m.CountriesPage })),
    name: 'Countries',
    description: 'Manage ISO 3166-1 country codes',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'Globe', size: 20 }, showInNav: true, order: 4, audience: 'internal' },
  },
  {
    path: '/commodities',
    component: () => import('./pages/CommoditiesPage').then((m) => ({ default: m.CommoditiesPage })),
    name: 'Commodities',
    description: 'Manage Harmonized System 2022 headings (4-digit)',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'Package', size: 20 }, showInNav: true, order: 5, audience: 'internal' },
  },
  {
    path: '/charge-types',
    component: () => import('./pages/ChargeTypesPage').then((m) => ({ default: m.ChargeTypesPage })),
    name: 'Charge Types',
    description: 'Manage the freight charge/accessorial catalog used when carriers quote',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'ReceiptText', size: 20 }, showInNav: true, order: 6, audience: 'internal' },
  },
  {
    path: '/hazard-classes',
    component: () => import('./pages/HazardClassesPage').then((m) => ({ default: m.HazardClassesPage })),
    name: 'Hazard Classes',
    description: 'Manage dangerous-goods classes and divisions',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'Flame', size: 20 }, showInNav: true, order: 7, audience: 'internal' },
  },
  {
    path: '/un-numbers',
    component: () => import('./pages/UnNumbersPage').then((m) => ({ default: m.UnNumbersPage })),
    name: 'UN Numbers',
    description: 'Manage UN dangerous-goods numbers',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'Hash', size: 20 }, showInNav: true, order: 8, audience: 'internal' },
  },
  {
    // `location` is a registered category whose admin screen lives in Freight
    // RFQ (its editor needs the port picker). Nothing in this section said so,
    // so the category looked unimplemented — this is the signpost
    // (QA ledger MD-010).
    path: '/locations',
    component: () =>
      import('./pages/LocationsPointerPage').then((m) => ({ default: m.LocationsPointerPage })),
    name: 'Locations',
    description: 'Reusable pickup/delivery locations — managed in Freight RFQ',
    permissions: ['master-data:item:manage'],
    nav: { icon: { name: 'MapPin', size: 20 }, showInNav: true, order: 9, audience: 'internal' },
  },
  {
    path: '/ports',
    component: () => import('./pages/PortsPage').then((m) => ({ default: m.PortsPage })),
    name: 'Ports',
    description: 'UN/LOCODE seaports, airports and inland container depots',
    // ':read', not ':manage' like its siblings — the register is a reference
    // directory every internal user benefits from searching. Writing it is
    // platform-admin only, gated in the page and enforced by the backend.
    permissions: ['master-data:item:read'],
    nav: { icon: { name: 'Anchor', size: 20 }, showInNav: true, order: 10, audience: 'internal' },
  },
]);
